"use client";

import React, { useEffect, useState } from "react";
import { Menu, Phone, Shield } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-9 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-20 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex-row-reverse gap-4 md:gap-6 lg:gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-white font-default font-bold text-[16px] transition-all hover:text-accent relative py-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-accent after:transition-all hover:after:w-full text-right">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <div className="h-9 w-9 bg-muted/20 animate-pulse rounded-md md:hidden" />;
  }
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground">
          <Menu className="size-6" />
          <span className="sr-only">فتح القائمة</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="bg-secondary/95 backdrop-blur-xl w-[300px] sm:w-[400px] border-r border-white/10 text-white">
        <SheetTitle className="sr-only">قائمة التنقل</SheetTitle>
        <div className="flex flex-col gap-8 mt-8">
          <Link to="/" onClick={() => setIsOpen(false)} className="text-right inline-flex items-center gap-1">
            <Shield className="size-6 text-accent" />
            <span className="text-2xl font-bold text-white font-default">سرايا</span>
          </Link>
          <nav className="flex flex-col gap-2">
            {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-white font-default font-bold text-lg py-4 border-b border-white/5 transition-colors hover:text-accent text-right" onClick={() => setIsOpen(false)} data-index={i}>
                {item.label}
              </Link>)}
          </nav>
          <div className="mt-4">
            <Button className="w-full bg-accent hover:bg-accent/90 text-primary gap-2 font-default font-bold shadow-lg h-8 sm:h-9 px-3 sm:px-4 text-lg rounded-full transition-transform active:scale-95">
              <Phone className="size-5" />
              اتصل بنا الآن
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="222698" id="header" dir="rtl" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-primary/80 backdrop-blur-md border-b border-white/10 shadow-xl" : "bg-transparent border-b border-transparent"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 relative z-10 text-right">
          <Link to="#" className="inline-flex items-center gap-1">
            <Shield className="size-6 text-accent" />
            <span className="text-xl sm:text-2xl font-bold text-white font-default">سرايا</span>
          </Link>
        </div>

        {}
        <MenuProvider menu_id="46" className="hidden md:flex flex-1 justify-end px-8 relative z-10">
          <DesktopMenu />
        </MenuProvider>

        {}
        <div className="flex items-center gap-4 relative z-10">
          <div className="relative hidden sm:inline-flex items-center justify-center">
            <span className="absolute inset-0 rounded-full ring-2 ring-accent animate-ping opacity-40 duration-1000"></span>
            <Button className="relative bg-accent hover:bg-accent/90 text-primary gap-2 font-default font-bold shadow-lg z-10 rounded-full transition-all hover:scale-105 hover:shadow-accent/20">
              <Phone className="size-4" />
              <span className="hidden lg:inline">تواصل معنا</span>
              <span className="lg:hidden">اتصل</span>
            </Button>
          </div>

          {}
          <MenuProvider menu_id="46" className="md:hidden flex items-center">
            <MobileMenu />
          </MenuProvider>
        </div>
      </div>
    </section>;
}